// reviews-slider.js — mobile infinite slider for the review cards
//
// Shared by sections/home-reviews.liquid and sections/product-reviews-grid.liquid,
// which render the same card row. On desktop the row stays a plain grid; at
// ≤768px, when the section's "Slider on mobile" setting is on, the row becomes a
// native scroll-snap track so swiping is handled by the browser.
//
// The loop: every real card is cloned once before and once after the real set.
// When the track settles on a clone, scrollLeft is moved — without animation —
// to the matching real card, so there is always another card in both
// directions. scroll-snap-stop: always (in the SCSS) keeps a hard flick from
// skipping past the clone set before that jump happens.
//
// Markup contract:
//   [data-reviews-track]             the card row; data-autoplay / data-interval
//     > [data-slide]                 one per review
//   [data-reviews-dots]              empty container in the same parent
//
// Loaded by each section that uses it, so it can appear twice on a page; the
// guard below keeps the listeners single.

(function () {
  'use strict';

  if (window.__reviewsSlider) return;
  window.__reviewsSlider = true;

  var MOBILE = window.matchMedia('(max-width: 768px)');
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var STATE = new WeakMap();

  function realSlides(track) {
    return Array.prototype.filter.call(track.children, function (el) {
      return el.hasAttribute('data-slide') && !el.hasAttribute('data-clone');
    });
  }

  function makeClone(slide) {
    var clone = slide.cloneNode(true);
    clone.setAttribute('data-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    // The theme editor maps blocks by these; a clone must not claim the block.
    clone.removeAttribute('data-shopify-editor-block');
    clone.removeAttribute('id');
    return clone;
  }

  // Scroll offset that puts card i at the snap edge. Measured from the first
  // card rather than the track, so the track's own side padding cancels out.
  function pos(state, i) {
    return state.all[i].offsetLeft - state.all[0].offsetLeft;
  }

  function nearest(state) {
    var left = state.track.scrollLeft;
    var best = 0;
    var bestDist = Infinity;
    for (var i = 0; i < state.all.length; i++) {
      var d = Math.abs(pos(state, i) - left);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return best;
  }

  function jump(state, i) {
    state.track.scrollLeft = pos(state, i);
  }

  function goTo(state, i) {
    state.track.scrollTo({ left: pos(state, i), behavior: 'smooth' });
  }

  function setActive(state, k) {
    if (state.active === k) return;
    state.active = k;
    state.dots.forEach(function (dot, j) {
      dot.classList.toggle('active', j === k);
      dot.setAttribute('aria-current', j === k ? 'true' : 'false');
    });
  }

  // Runs once scrolling has stopped: hop off a clone onto its real twin.
  function settle(state) {
    if (state.touching) return;
    var i = nearest(state);
    var n = state.n;
    if (i < n) jump(state, i + n);
    else if (i >= n * 2) jump(state, i - n);
    setActive(state, i % n);
  }

  function stopAutoplay(state) {
    if (state.timer) {
      clearInterval(state.timer);
      state.timer = null;
    }
  }

  function startAutoplay(state) {
    var track = state.track;
    if (track.getAttribute('data-autoplay') !== 'true' || REDUCED_MOTION.matches) return;
    var seconds = parseFloat(track.getAttribute('data-interval')) || 5;
    state.timer = setInterval(function () {
      if (document.hidden) return;
      goTo(state, nearest(state) + 1);
    }, seconds * 1000);
  }

  function buildDots(state, holder) {
    if (!holder) return [];
    holder.innerHTML = '';
    var dots = [];
    for (var k = 0; k < state.n; k++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'reviews-dot';
      dot.setAttribute('aria-label', 'Review ' + (k + 1) + ' of ' + state.n);
      dot.setAttribute('data-index', k);
      holder.appendChild(dot);
      dots.push(dot);
    }
    holder.hidden = false;
    return dots;
  }

  function enable(track) {
    if (STATE.has(track)) return;
    var slides = realSlides(track);
    var n = slides.length;
    if (n < 2) return;

    var before = document.createDocumentFragment();
    var after = document.createDocumentFragment();
    slides.forEach(function (s) {
      before.appendChild(makeClone(s));
      after.appendChild(makeClone(s));
    });
    track.insertBefore(before, track.firstChild);
    track.appendChild(after);

    var state = {
      track: track,
      slides: slides,
      n: n,
      all: Array.prototype.filter.call(track.children, function (el) {
        return el.hasAttribute('data-slide');
      }),
      holder: track.parentElement.querySelector('[data-reviews-dots]'),
      active: -1,
      touching: false,
      timer: null,
      settleTimer: null
    };
    state.dots = buildDots(state, state.holder);

    state.onScroll = function () {
      setActive(state, nearest(state) % n);
      clearTimeout(state.settleTimer);
      state.settleTimer = setTimeout(function () {
        settle(state);
      }, 120);
    };
    state.onTouchStart = function () {
      state.touching = true;
      stopAutoplay(state);
    };
    state.onTouchEnd = function () {
      state.touching = false;
      clearTimeout(state.settleTimer);
      state.settleTimer = setTimeout(function () {
        settle(state);
      }, 120);
    };
    state.onDotClick = function (e) {
      var dot = e.target.closest('.reviews-dot');
      if (!dot) return;
      stopAutoplay(state);
      goTo(state, n + parseInt(dot.getAttribute('data-index'), 10));
    };

    track.addEventListener('scroll', state.onScroll, { passive: true });
    track.addEventListener('touchstart', state.onTouchStart, { passive: true });
    track.addEventListener('touchend', state.onTouchEnd, { passive: true });
    track.addEventListener('touchcancel', state.onTouchEnd, { passive: true });
    track.addEventListener('pointerdown', state.onTouchStart);
    track.addEventListener('pointerup', state.onTouchEnd);
    if (state.holder) state.holder.addEventListener('click', state.onDotClick);

    track.setAttribute('role', 'region');
    track.setAttribute('aria-roledescription', 'carousel');
    track.classList.add('is-looping');

    STATE.set(track, state);
    jump(state, n);
    setActive(state, 0);
    startAutoplay(state);
  }

  function disable(track) {
    var state = STATE.get(track);
    if (!state) return;
    stopAutoplay(state);
    clearTimeout(state.settleTimer);

    track.removeEventListener('scroll', state.onScroll);
    track.removeEventListener('touchstart', state.onTouchStart);
    track.removeEventListener('touchend', state.onTouchEnd);
    track.removeEventListener('touchcancel', state.onTouchEnd);
    track.removeEventListener('pointerdown', state.onTouchStart);
    track.removeEventListener('pointerup', state.onTouchEnd);
    if (state.holder) {
      state.holder.removeEventListener('click', state.onDotClick);
      state.holder.innerHTML = '';
      state.holder.hidden = true;
    }

    Array.prototype.slice.call(track.querySelectorAll('[data-clone]')).forEach(function (c) {
      c.remove();
    });
    track.removeAttribute('role');
    track.removeAttribute('aria-roledescription');
    track.classList.remove('is-looping');
    track.scrollLeft = 0;
    STATE.delete(track);
  }

  function sync(track) {
    if (MOBILE.matches) enable(track);
    else disable(track);
  }

  function syncAll(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-reviews-track]'), sync);
  }

  function onMediaChange() {
    syncAll();
  }

  if (MOBILE.addEventListener) MOBILE.addEventListener('change', onMediaChange);
  else MOBILE.addListener(onMediaChange);

  // Card widths are a percentage of the viewport, so a rotation moves every
  // snap point; re-seat each track on the card it was showing.
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      Array.prototype.forEach.call(document.querySelectorAll('[data-reviews-track]'), function (track) {
        var state = STATE.get(track);
        if (state) jump(state, state.n + Math.max(state.active, 0));
      });
    }, 150);
  });

  // Theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    syncAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    Array.prototype.forEach.call(e.target.querySelectorAll('[data-reviews-track]'), disable);
  });
  document.addEventListener('shopify:block:select', function (e) {
    var track = e.target.closest && e.target.closest('[data-reviews-track]');
    var state = track && STATE.get(track);
    if (!state) return;
    var k = state.slides.indexOf(e.target);
    if (k < 0) return;
    stopAutoplay(state);
    goTo(state, state.n + k);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      syncAll();
    });
  } else {
    syncAll();
  }
})();
